// @flow
import {
  useState,
  Fragment,
  useRef,
  useEffect,
  useLayoutEffect,
  type Node
} from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import {
  animated,
  useTrail,
  config as springConfigs
} from 'react-spring/web.cjs';
import { faGlobe } from '@fortawesome/pro-light-svg-icons/faGlobe';
import {
  COLORS,
  color,
  SPACING,
  typography,
  FONT_WEIGHTS
} from '@tablekit/theme';
import Button from '@tablekit/button';
import Icon from '@tablekit/icon';
import StoryblokEditor from 'components/editable/StoryblokEditor';
import StoryblokLink from 'components/Link';
import Link from 'components/GatsbyLink';
import useOnScroll from 'utils/useOnScroll';
import {
  MAX_WIDTH,
  GRID_GUTTER,
  MOBILE_MAX_WIDTH,
  Z_INDICES
} from 'utils/theme';
import { ROOT_LOCALE, USER_LANGUAGE_STORAGE_KEY } from 'utils/constants';
import { safeLink } from 'utils/safeLink';
import type {
  BlokComponentType,
  LinkListType,
  LinkType
} from 'components/types';
import componentKeys from '../../componentKeys';
import { CountryLabel, Logo, MenuHeader, MenuItem } from './styles';
import type { NavPropsType } from './types';
import { Positioner } from './MobileNavToggle';
import DesktopDropMenu, { DropMenuItem } from './DesktopDropMenu';
import { imageTransform } from '../Image';

const DESKTOP_NAVBAR_OFFSET = parseInt(SPACING.L8, 10);
const NAVBAR_SHADOW_SIZE = '20px';

const NavBarPositioner = styled.div`
  left: 0;
  right: 0;
  max-width: 100vw;
  transition-property: background-color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  z-index: ${Z_INDICES.NAV};
  &:after {
    content: '';
    position: absolute;
    z-index: -1;
    bottom: 0;
    // if no height the box-shadow becomes invisible
    height: 10px;
    left: 0;
    right: 0;
    margin: 0 -${NAVBAR_SHADOW_SIZE};
    box-shadow: 0px 0px ${NAVBAR_SHADOW_SIZE} 0px ${COLORS.GRAY.L5};
    transition-property: opacity;
    transition-duration: inherit;
    transition-timing-function: inherit;
  }
  ${(props) =>
    props.isFixed
      ? css`
          position: fixed;
          top: 0;
          background: ${COLORS.GRAY.L1};
          &:after {
            opacity: 1;
          }
          & ~ ${Positioner} {
            color: ${color('primary')(props)};
          }
        `
      : css`
          ${props.isMobileToggleVisible
            ? css`
                position: fixed;
                top: 0;
              `
            : css`
                position: absolute;
                top: ${DESKTOP_NAVBAR_OFFSET}px;
              `}

          background: transparent;
          &:after {
            opacity: 0;
          }
          & ~ ${Positioner} {
            color: white;
          }
        `};
`;

const NavBar = styled.div`
  transition-property: color;
  transition-duration: inherit;
  transition-timing-function: inherit;
  ${(props) =>
    props.isFixed
      ? css`
          color: ${color('text')(props)};
        `
      : css`
          color: ${COLORS.GRAY.L1};
        `};

  display: flex;
  justify-content: space-between;
  align-items: stretch;
  max-width: ${MAX_WIDTH};
  padding: ${SPACING.L4};

  @media (min-width: 576px) {
    padding: ${SPACING.L4} ${GRID_GUTTER};
  }
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const NavBarWhiteBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: ${COLORS.GRAY.L1};
  transition-property: opacity;
  transition-duration: inherit;
  transition-timing-function: inherit;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
`;

const OverlayLogo = styled(Logo)`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
`;

export const HEADER_HEIGHT = 56;

const CustomLogo = styled(Logo)`
  margin: -${SPACING.L4} 0;
  height: ${({ useTallHeader }) =>
    useTallHeader ? HEADER_HEIGHT * 2 : HEADER_HEIGHT}px;
`;

const MENU_MOBILE_TOGGLE_OFFSET = SPACING.L6;
const Menu = styled.div`
  display: flex;
  align-items: stretch;
  margin: -${SPACING.L4} 0;
  flex: 1 1 100%;
  padding-right: ${({ isMobileToggleVisible }) =>
    isMobileToggleVisible ? MENU_MOBILE_TOGGLE_OFFSET : 0};
`;

const NavItemsMenu = styled.div`
  display: flex;
  flex: 1 1 0%;
  position: relative;
  ${({ isActive }) =>
    isActive ? '' : 'overflow: hidden; pointer-events: none;'}
`;

const NavItemsPositioner = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  flex: 1 1 100%;
`;

const ActionsMenu = styled.div`
  display: flex;
  align-items: center;
  ${({ isActive }) =>
    isActive ? '' : 'overflow: hidden; pointer-events: none;'}
`;

export const MenuStoryblokLink = MenuHeader.withComponent(StoryblokLink);

const DropStaticLink = styled.a`
  padding: ${SPACING.L4};
  display: inline-flex;
  align-items: center;
  ${typography('L2')};
  font-weight: ${FONT_WEIGHTS.REGULAR};

  &:hover {
    background-color: ${COLORS.GRAY.L2};
  }

  &,
  &:hover,
  &:visited,
  &:active {
    outline: none;
    color: currentColor;
    text-decoration: none;
  }
`;

const DropLink = DropStaticLink.withComponent(Link);
const DropStoryblokLink = styled(DropStaticLink.withComponent(StoryblokLink))`
  display: flex;
  flex: 1 1 100%;
`;

const LogoWrapper = styled.div`
  position: relative;
  display: flex;
`;

const renderNavItem = (
  navItem: BlokComponentType<LinkListType | LinkType>,
  hoveredMenu: string,
  createToggleOpen: (string) => () => void,
  isDark: boolean
): Node => {
  const { _uid: id } = navItem;
  const isOpen = id === hoveredMenu;
  if (navItem.component === componentKeys.LINK) {
    const onMouseEnter = createToggleOpen(id);
    const {
      value: link,
      label
    } = ((navItem: any): BlokComponentType<LinkType>);
    return (
      <MenuItem onMouseEnter={onMouseEnter}>
        <MenuStoryblokLink isDark={isDark} isOpen={isOpen} link={link}>
          {label}
        </MenuStoryblokLink>
      </MenuItem>
    );
  }
  if (navItem.component === componentKeys.LINK_LIST) {
    const { items, title } = ((navItem: any): BlokComponentType<LinkListType>);
    return (
      <DesktopDropMenu
        id={id}
        isOpen={isOpen}
        createToggleOpen={createToggleOpen}
        header={<Fragment>{title}</Fragment>}
        items={items}
        itemsRenderer={(item, index, ref) => (
          <DropMenuItem
            key={item._uid}
            ref={ref}
            index={index}
            itemCount={items.length}
          >
            <DropStoryblokLink link={item.value}>
              {item.label}
            </DropStoryblokLink>
          </DropMenuItem>
        )}
      />
    );
  }
  return null;
};

const NAV_STATES = Object.freeze({
  LOADING: 'loading',
  FULL: 'full',
  ACTIONS_ONLY: 'actions',
  MOBILE: 'mobile'
});

const Desktop = (
  props: NavPropsType & {
    isMobileToggleVisible: boolean,
    setIsMobileToggleVisible: (boolean) => void
  }
) => {
  const {
    homeUrl,
    countries,
    country,
    header: {
      action_buttons: actionButtons = [],
      header_logo: [logo],
      header_logo_link: logoLink,
      hide_language_switcher: hideLanguageSwitcher,
      navigation_items: navItems
    },
    isWhiteOnly,
    countryLinks,
    isMobileToggleVisible,
    setIsMobileToggleVisible,
    customHeaderConfig
  } = props;
  const [, homeUrlRoot, ...homeUrlRest] = homeUrl.split('/');
  const menuElementRef = useRef();
  const navElementsWidthRef = useRef();
  const actionElementsWidthRef = useRef();
  const animationRequestRef = useRef();

  const defaultNavState =
    typeof window !== 'undefined' ? window.topNavState : undefined;
  const [navState, internalSetNavState] = useState(
    defaultNavState || NAV_STATES.LOADING
  );
  const setNavState = (newState) => {
    window.topNavState = newState;
    internalSetNavState(newState);
  };
  const [hoveredMenu, setHoveredMenu] = useState('');
  const createToggleOpen = (key) => () => setHoveredMenu(key);
  const isOpen = (key) => hoveredMenu === key;
  const [isFixedNav, setIsFixedNav] = useState(false);

  useOnScroll(() => {
    let isScrolledAfterNavBar = false;
    const windowWidth = Math.min(window.outerWidth, window.innerWidth);
    if (windowWidth <= MOBILE_MAX_WIDTH) {
      isScrolledAfterNavBar = window.pageYOffset > 0;
    } else {
      isScrolledAfterNavBar = window.pageYOffset > DESKTOP_NAVBAR_OFFSET;
    }
    if (isFixedNav !== isScrolledAfterNavBar) {
      setIsFixedNav(isScrolledAfterNavBar);
    }
  }, [isFixedNav]);

  const menuButtons = actionButtons || [];
  const isFixed =
    isWhiteOnly ||
    (customHeaderConfig && customHeaderConfig.useTallHeader) ||
    isFixedNav;
  const isNavItemsShown = navState === NAV_STATES.FULL;
  const isActionsMenuShown =
    navState === NAV_STATES.FULL || navState === NAV_STATES.ACTIONS_ONLY;

  let logoImage = (
    <Fragment>
      <Logo
        isVisible={!isFixed}
        src={imageTransform(logo.white, { size: { height: 24 } }, false)}
        alt={logo.alt}
      />
      <OverlayLogo
        isVisible={isFixed}
        src={imageTransform(logo.colored, { size: { height: 24 } }, false)}
        alt={logo.alt}
      />
    </Fragment>
  );
  if (customHeaderConfig && customHeaderConfig.headerLogo) {
    logoImage = (
      <CustomLogo
        isVisible
        useTallHeader={customHeaderConfig.useTallHeader}
        src={customHeaderConfig.headerLogo}
        alt={logo.alt}
      />
    );
  }
  let logoElement = <Link to={homeUrl}>{logoImage}</Link>;
  if (logoLink) {
    logoElement = (
      <MenuStoryblokLink link={logoLink}>{logoImage}</MenuStoryblokLink>
    );
  }

  let countryLabel;
  if (country) {
    countryLabel = country.code.toUpperCase();
  }
  if (!countryLabel) {
    const foundCountry = countries.find(({ code }) => code === homeUrlRoot);
    if (foundCountry) {
      countryLabel = foundCountry.code.toUpperCase();
    } else {
      countryLabel = homeUrlRoot.toUpperCase();
    }
  }
  const countrySelectElement =
    hideLanguageSwitcher || countries.length <= 1 ? null : (
      <DesktopDropMenu
        id="country-select"
        isOpen={isOpen('country-select')}
        createToggleOpen={createToggleOpen}
        header={
          <Fragment>
            <Icon icon={faGlobe} />{' '}
            <CountryLabel>
              {countryLabel || ROOT_LOCALE.toUpperCase()}
            </CountryLabel>
          </Fragment>
        }
        items={countries}
        itemsRenderer={({ code, name }, index, ref) => {
          if (code === homeUrlRoot) {
            return null;
          }
          const localeProps = {
            to: undefined,
            rel: undefined,
            key: code,
            hrefLang: code,
            onClick: () => {
              localStorage.setItem(USER_LANGUAGE_STORAGE_KEY, code);
            }
          };
          // $FlowFixMe
          const countryLink = countryLinks?.alternates?.find(
            ({ lang }) => lang === code
          );
          if (countryLink) {
            localeProps.to = countryLink.slug;
            if (!countryLink.isSoft) {
              localeProps.rel = 'alternate';
            }
          } else {
            localeProps.to = safeLink(`/${code}/${homeUrlRest.join('/')}/`);
          }
          if (typeof window !== 'undefined') {
            localeProps.to = `${localeProps.to}${window.location.search}${window.location.hash}`;
          }
          if (code === 'en') {
            return (
              <DropStaticLink
                key={localeProps.key}
                href={localeProps.to}
                rel={localeProps.rel}
                hrefLang={localeProps.hrefLang}
                onClick={localeProps.onClick}
              >
                {name}
              </DropStaticLink>
            );
          }
          return (
            <DropLink {...localeProps} ref={ref}>
              {name}
            </DropLink>
          );
        }}
      />
    );

  const navItemsTrail = useTrail(navItems.length, {
    opacity: isNavItemsShown ? 1 : 0,
    x: isNavItemsShown ? 0 : -20,
    from: navState === NAV_STATES.LOADING ? { opacity: 0, x: -20 } : undefined,
    config: isNavItemsShown
      ? { mass: 1, tension: 500, friction: 80 }
      : springConfigs.default
  });

  const navItemElements = navItemsTrail.map(({ x, ...rest }, index) => (
    <animated.div
      key={navItems[index]._uid}
      style={{
        ...rest,
        transform: x.interpolate((xVal) => `translate3d(0, ${xVal}px, 0)`)
      }}
    >
      {renderNavItem(navItems[index], hoveredMenu, createToggleOpen, isFixed)}
    </animated.div>
  ));

  const actionsMenuTrail = useTrail(
    actionButtons.length + (countrySelectElement ? 1 : 0),
    {
      opacity: isActionsMenuShown ? 1 : 0,
      x: isActionsMenuShown ? 0 : -20,
      from:
        navState === NAV_STATES.LOADING ? { opacity: 0, x: -20 } : undefined,
      config: isActionsMenuShown
        ? { mass: 1, tension: 500, friction: 80 }
        : springConfigs.default
    }
  );

  function handleMenuResize() {
    if (
      !menuElementRef.current ||
      navElementsWidthRef.current === undefined ||
      actionElementsWidthRef.current === undefined
    ) {
      return;
    }
    if (animationRequestRef.current) {
      window.cancelAnimationFrame(animationRequestRef.current);
    }
    animationRequestRef.current = window.requestAnimationFrame(() => {
      if (
        !menuElementRef.current ||
        navElementsWidthRef.current === undefined ||
        actionElementsWidthRef.current === undefined
      ) {
        return;
      }
      const outerWidth = menuElementRef.current.offsetWidth;
      const innerWidth =
        navElementsWidthRef.current + actionElementsWidthRef.current;
      if (
        outerWidth < 420 ||
        actionElementsWidthRef.current >
          outerWidth -
            parseInt(MENU_MOBILE_TOGGLE_OFFSET, 10) -
            parseInt(SPACING.L6, 10)
      ) {
        setNavState(NAV_STATES.MOBILE);
        setIsMobileToggleVisible(true);
      } else if (navState === NAV_STATES.LOADING) {
        // Dimensions are different on first load, don't know why
        if (innerWidth < outerWidth - 32) {
          setNavState(NAV_STATES.FULL);
          setIsMobileToggleVisible(false);
        } else {
          setNavState(NAV_STATES.ACTIONS_ONLY);
          setIsMobileToggleVisible(true);
        }
      } else if (innerWidth > outerWidth - 32) {
        // the offsets here and in the next condition are to prevent infinite toggling
        // due to the reduced padding of "Mobile" nav mode
        setNavState(NAV_STATES.ACTIONS_ONLY);
        setIsMobileToggleVisible(true);
      } else if (innerWidth < outerWidth - 128) {
        setNavState(NAV_STATES.FULL);
        setIsMobileToggleVisible(false);
      }
    });
  }

  useLayoutEffect(() => {
    handleMenuResize();
  }, []);

  useEffect(() => {
    function resizeHandler() {
      handleMenuResize();
    }
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, [setIsMobileToggleVisible, setNavState, navState]);

  const actionMenuElements = actionsMenuTrail.map(({ x, ...rest }, index) => {
    let contentElement;
    let key;
    if (index === 0 && countrySelectElement) {
      key = 'country-select';
      contentElement = countrySelectElement;
    } else {
      const { _uid: id, label, value, is_app_link: isAppLink } = menuButtons[
        countrySelectElement ? index - 1 : index
      ];
      key = id;
      const component = isAppLink ? undefined : StoryblokLink;
      let buttonProps;
      if (isAppLink) {
        buttonProps = { href: CONFIG.appLoginUrl };
      } else {
        buttonProps = { link: value };
      }
      contentElement = (
        <MenuItem>
          <Button
            size="small"
            appearance={isFixed ? 'outline' : 'inverse'}
            component={component}
            {...buttonProps}
          >
            {label}
          </Button>
        </MenuItem>
      );
    }
    return (
      <animated.div
        key={key}
        style={{
          ...rest,
          transform: x.interpolate((xVal) => `translate3d(0, ${xVal}px, 0)`)
        }}
      >
        {contentElement}
      </animated.div>
    );
  });

  const hasMenuItems = navItems.length || actionButtons.length;

  return (
    <NavBarPositioner
      isFixed={isFixed}
      isMobileToggleVisible={isMobileToggleVisible && hasMenuItems}
    >
      <NavBarWhiteBackground isVisible={isFixed} />
      <NavBar isFixed={isFixed} isMobileToggleVisible={isMobileToggleVisible}>
        <StoryblokEditor content={logo}>
          <LogoWrapper>{logoElement}</LogoWrapper>
        </StoryblokEditor>
        <Menu
          ref={(ref) => {
            if (!ref) return;
            menuElementRef.current = ref;
          }}
          onMouseLeave={() => setHoveredMenu('')}
          isMobileToggleVisible={isMobileToggleVisible && hasMenuItems}
        >
          <NavItemsMenu isActive={isNavItemsShown}>
            <NavItemsPositioner
              ref={(ref) => {
                if (!ref || navElementsWidthRef.current) return;
                navElementsWidthRef.current = ref.offsetWidth;
              }}
            >
              {navItemElements}
            </NavItemsPositioner>
          </NavItemsMenu>
          <ActionsMenu
            isActive={isActionsMenuShown}
            ref={(ref) => {
              if (!ref || actionElementsWidthRef.current) return;
              actionElementsWidthRef.current = ref.offsetWidth;
            }}
          >
            {actionMenuElements}
          </ActionsMenu>
        </Menu>
      </NavBar>
    </NavBarPositioner>
  );
};

export default Desktop;
